import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const studentFeatures = [
  { label: '核心概念', desc: '本节课的关键知识点和理论框架', icon: 'ri-lightbulb-line' },
  { label: '关键案例', desc: '案例链及其与理论的对应关系', icon: 'ri-stack-line' },
  { label: '问题链', desc: '教师提出的核心问题及追问路径', icon: 'ri-question-answer-line' },
  { label: '理论链', desc: '从概念到理论体系的逻辑路径', icon: 'ri-git-branch-line' },
  { label: '课后复习提示', desc: '汇总学生常见疑问，生成复习重点', icon: 'ri-file-list-3-line' },
  { label: '课堂测验', desc: '基于课堂内容的自测题目', icon: 'ri-checkbox-circle-line' },
];

export default function P19EvidenceStudent({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Left - Title & intro */}
          <div className="lg:col-span-4">
            <div ref={titleRef}>
              <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                服务学生
              </span>
              <h2 className={`font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                证据<span className="text-primary-600">对学生</span>的价值
              </h2>
              <p className={`text-sm text-foreground-500 font-body leading-relaxed mb-6 transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
                学生端可以把AI评课的结果转化为智能助教，不是在评价老师，而是在帮自己回看学习过程。
              </p>
            </div>

            {/* Smart assistant card */}
            <div className={`p-5 rounded-2xl glass-liquid border-primary-200/30 transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl glass-blue border-primary-200/40 flex items-center justify-center">
                    <i className="ri-robot-2-line text-2xl text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-heading text-base font-bold text-foreground-800">智能助教</h4>
                    <p className="text-xs text-foreground-400 font-body">课后自动生成</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 font-body leading-relaxed">
                  对学生来说，AI不是在评价老师，而是帮自己回看学习过程。老师的反馈又能反过来提醒下一次课堂哪些概念需要再讲透。
                </p>
              </div>
            </div>
          </div>

          {/* Right - Feature grid */}
          <div className="lg:col-span-8">
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              {studentFeatures.map((feat, i) => (
                <div
                  key={feat.label}
                  className="group p-5 rounded-2xl glass border-white/60 hover:border-primary-200/50 transition-all duration-300 hover:scale-[1.02] flex items-start gap-4"
                  style={{ transitionDelay: `${500 + i * 80}ms` }}
                >
                  <div className="w-10 h-10 rounded-xl glass-blue border-primary-200/40 flex items-center justify-center flex-shrink-0">
                    <i className={`${feat.icon} text-base text-primary-600`} />
                  </div>
                  <div>
                    <h4 className="font-heading text-sm md:text-base font-bold text-foreground-800 mb-1">{feat.label}</h4>
                    <p className="text-xs text-foreground-500 font-body leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}